import { api } from '../../lib/api';
import type {
  MetaGoalBucket,
  PortalComparisonMode,
  PortalGoogleReport,
  PortalGrouping,
  PortalMetaReport,
  PortalOverviewReport,
} from '../../types/portalReport';
import type { PortalWebsiteReport } from '../../types/portalWebsite';
import type { ReportTemplate } from '../../types/reportTemplate';

// CLIENT PORTAL round 2, item 7 — the STAFF data path for "view as client".
//
// ⚠️⚠️ DELIBERATELY A SEPARATE FILE FROM THE PORTAL'S OWN `portal/report/
// reportApi.ts`, and it must stay that way. These calls go to
// /api/client-preview/* behind the STAFF guard; the portal's go to /api/portal/*
// behind requirePortalAuth. Two data paths, one set of report components, no
// shared auth surface — a staff page importing the portal's fetcher would send
// staff credentials at a client endpoint, which is exactly what item 7 forbids.
//
// A source-shape test asserts the staff page never imports the portal fetcher.

export interface ClientPreviewParams {
  /** ⚠️ Required — there is no agency-wide preview. */
  accountId: string;
  from: string;
  to: string;
  grouping: PortalGrouping;
  today: string;
  campaignIds?: string[];
  comparison?: PortalComparisonMode;
  /** The goal-split Meta page's goal filter. `undefined` = All Campaigns. */
  goalBucket?: MetaGoalBucket;
  /** Website Analytics' studio picker. A request only — the server decides. */
  studio?: string | null;
  /**
   * "Preview as" — render the report under this template WITHOUT setting it. A
   * request only; preview only; it never writes (the server route has no write).
   * `undefined` = the account's own resolved template.
   */
  template?: ReportTemplate;
}

function toQuery(params: ClientPreviewParams): string {
  const q = new URLSearchParams({
    accountId: params.accountId,
    from: params.from,
    to: params.to,
    grouping: params.grouping,
    today: params.today,
  });
  if (params.campaignIds !== undefined) q.set('campaignIds', params.campaignIds.join(','));
  if (params.comparison) q.set('comparison', params.comparison);
  // ⚠️ `goal` on the wire — the name the router's schema parses (reportApi.ts
  // does the same for the portal). Absent means All; never `goal=` or `goal=all`.
  if (params.goalBucket) q.set('goal', params.goalBucket);
  if (params.studio) q.set('studio', params.studio);
  if (params.template) q.set('template', params.template);
  return q.toString();
}

export async function fetchClientPreviewOverview(
  params: ClientPreviewParams,
): Promise<PortalOverviewReport> {
  return api<PortalOverviewReport>(`/api/client-preview/report/overview?${toQuery(params)}`);
}

export async function fetchClientPreviewMeta(
  params: ClientPreviewParams,
): Promise<PortalMetaReport> {
  return api<PortalMetaReport>(`/api/client-preview/report/meta?${toQuery(params)}`);
}

export async function fetchClientPreviewGoogle(
  params: ClientPreviewParams,
): Promise<PortalGoogleReport> {
  return api<PortalGoogleReport>(`/api/client-preview/report/google?${toQuery(params)}`);
}

export async function fetchClientPreviewWebsite(
  params: ClientPreviewParams,
): Promise<PortalWebsiteReport> {
  return api<PortalWebsiteReport>(`/api/client-preview/report/website?${toQuery(params)}`);
}
